import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { EmailVerification } from "../../../../api/graphql";
import { EmailVerificationListStore } from "../../store";
import { userDisplayId } from "../../../user/store/UserCommonStore";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: EmailVerificationListStore };

export const EmailVerificationListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<EmailVerification>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: EmailVerification, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: EmailVerification, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: EmailVerification) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: EmailVerification, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<EmailVerification>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "status") {
          return {
            ...col,
            id: "status",
            accessorFn: (row: EmailVerification) => {
              const v = (row as any).status;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.EmailVerificationStatusOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "scope") {
          return {
            ...col,
            id: "scope",
            accessorFn: (row: EmailVerification) => {
              const v = (row as any).scope;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.EmailVerificationScopeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "expiresAt") {
          return {
            ...col,
            id: "expiresAt",
            accessorFn: (row: EmailVerification) =>
              row.expiresAt
                ? new Date(row.expiresAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "sentAt") {
          return {
            ...col,
            id: "sentAt",
            accessorFn: (row: EmailVerification) =>
              row.sentAt
                ? new Date(row.sentAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "verifiedAt") {
          return {
            ...col,
            id: "verifiedAt",
            accessorFn: (row: EmailVerification) =>
              row.verifiedAt
                ? new Date(row.verifiedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "lastSentAt") {
          return {
            ...col,
            id: "lastSentAt",
            accessorFn: (row: EmailVerification) =>
              row.lastSentAt
                ? new Date(row.lastSentAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "requestedBy") {
          return {
            ...col,
            id: "requestedBy",
            accessorFn: (row: EmailVerification) =>
              row.requestedBy ? userDisplayId(row.requestedBy, langStore) : "-",
          };
        }
        if (col.accessorKey === "validationSubmittedAt") {
          return {
            ...col,
            id: "validationSubmittedAt",
            accessorFn: (row: EmailVerification) =>
              row.validationSubmittedAt
                ? new Date(row.validationSubmittedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.emailVerificationCurrentPage || [];

    return (
      <div id={"ListOfEmailVerification"}>
        <div id={"StartOfListingOfEmailVerification"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
